import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { keyBy } from 'lodash';
import { useDispatch } from 'react-redux';
import { AccountAPI } from 'api/account';
import { AuditLogMetaName } from 'api/financials-audit-logs/type';
import { UserAPI } from 'api/user';
import { GENERAL_API_STALE } from 'config/query-config';
import { LS_TEMPLATE_ID_KEY, RoutePaths } from 'constant';
import { COGNITO_ENABLED_KEY } from 'constant/constant';
import { set } from 'data/account';
import { success } from 'data/current-user-profile';
import { personalTasksSlice } from 'data/personal-tasks/slice';
import { authService } from 'services/auth-service';
import { putAll as putAllCompanies } from 'stores/companies';
import { putAll as putAllPermissionSets } from 'stores/permission-sets';
import { update as updateVisibilitySettings } from 'stores/visibility';
import { currentUserState } from 'utils/current-user-state';
import { isLocalhost } from 'utils/is-localhost';
import { applyPastelTheme } from '../utils/pastel-color';
import { getQueryAdvancedSettingsConfigKey } from './advanced-settings/advanced-settings';
import { oneDay } from './constants';
import { HOLIDAY_CALENDAR_KEY } from './holiday-calendar';
export const ACCOUNT_META_DATA_QUERY_KEY = [
    'account-meta-data'
];
export const NOTIFICATION_DATA_QUERY_KEY = [
    'notification-data'
];
export const G2_QUERY_KEY = [
    'g2-data'
];
export const INIT_PROJECTS_QUERY_KEY = [
    'init-projects'
];
export const CURRENCY_MAPPING_LIST_KEY = [
    'CURRENCY_MAPPING_LIST'
];
const getCurrencyMappingHistoryKey = (currencyCode)=>[
        'CURRENCY_MAPPING_HISTORY',
        currencyCode
    ];
const getCustomCalendarConfigKey = ()=>[
        'CUSTOM_CALENDAR_CONFIG'
    ];
const getCurrencyMappingAuditLogKey = (currencyCode)=>[
        'CURRENCY_MAPPING_AUDIT_LOG',
        currencyCode
    ];
const commonOptions = {
    queryKey: ACCOUNT_META_DATA_QUERY_KEY,
    queryFn: ()=>AccountAPI.getInitData(),
    staleTime: oneDay,
    refetchOnWindowFocus: false
};
// This is a custom hook that will return the notification data via set timeout
// Based on this initial data we can update the pending notifications count in query client cache via websocket
export const useNotificationDataQuery = ()=>{
    return useQuery({
        queryKey: NOTIFICATION_DATA_QUERY_KEY,
        // promise with timeout
        queryFn: ()=>{
            return new Promise((resolve)=>{
                resolve({
                    pendingNotifications: 0,
                    newNotificationAvailable: false
                });
            });
        }
    });
};
export const useAccountDataQuery = ()=>{
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    return useQuery({
        ...commonOptions,
        queryFn: ()=>AccountAPI.getInitData().then((response)=>{
                var _account_metaData_theme;
                authService.setAuthState(true);
                const { account, accountMeta, accountConfig, userProfile, companies, permissionSetList, accountVisibilitySettings, personalTasks, holidayCalendar, newNotificationAvailable, showG2 } = response;
                queryClient.setQueryData(G2_QUERY_KEY, showG2);
                if (accountConfig) {
                    queryClient.setQueryData(getQueryAdvancedSettingsConfigKey(), accountConfig);
                }
                queryClient.setQueryData(NOTIFICATION_DATA_QUERY_KEY, {
                    // increment the pending notifications count by 1 when a new notification is received.
                    pendingNotifications: newNotificationAvailable ? 1 : 0,
                    // set the newNotificationAvailable property to false after the first render
                    newNotificationAvailable
                });
                const holidayCalendarCacheData = queryClient.getQueryData(HOLIDAY_CALENDAR_KEY);
                if (holidayCalendar && !holidayCalendarCacheData) {
                    queryClient.setQueryData(HOLIDAY_CALENDAR_KEY, keyBy(holidayCalendar, 'holidayCalendarId'), {
                        staleTime: GENERAL_API_STALE
                    });
                }
                if (userProfile.projects != null) {
                    queryClient.setQueryData(INIT_PROJECTS_QUERY_KEY, ()=>{
                        return {
                            projects: userProfile.projects,
                            byId: keyBy(userProfile.projects, 'projectId')
                        };
                    });
                }
                if ((_account_metaData_theme = account.metaData.theme) === null || _account_metaData_theme === void 0 ? void 0 : _account_metaData_theme.sideNavBg) {
                    var _account_metaData_theme1;
                    applyPastelTheme((_account_metaData_theme1 = account.metaData.theme) === null || _account_metaData_theme1 === void 0 ? void 0 : _account_metaData_theme1.sideNavBg);
                }
                dispatch(set(account));
                dispatch(success(userProfile));
                dispatch(putAllCompanies(companies !== null && companies !== void 0 ? companies : []));
                dispatch(putAllPermissionSets(permissionSetList));
                dispatch(personalTasksSlice.actions.fetchSuccess(personalTasks));
                if (accountVisibilitySettings) {
                    dispatch(updateVisibilitySettings(accountVisibilitySettings));
                }
                currentUserState.userState = {
                    currentUser: userProfile,
                    vendorCompany: accountMeta.company
                };
                // save in local storage for future use to avoid unnecessary api calls
                // since accountMeta is not going to change frequently
                // accountMeta will be cleared on logout
                // isLocalhost check id for dev purpose. Otherwise domain name has to be changed from inside the localstorage
                if (!isLocalhost()) {
                    localStorage.setItem('accountMeta', JSON.stringify(accountMeta));
                }
                return response;
            })
    });
};
export const ACCOUNT_CONFIG_QUERY_KEY = [
    'account-config'
];
export const useAccountConfigQuery = ()=>{
    return useQuery({
        queryKey: ACCOUNT_CONFIG_QUERY_KEY,
        queryFn: ()=>AccountAPI.getConfigData()
    });
};
export const useProjectsFromInitData = ()=>{
    const { data: initData, isLoading } = useAccountDataQuery();
    return useQuery(INIT_PROJECTS_QUERY_KEY, ()=>{
        var _initData_userProfile_permissions, _initData_userProfile;
        const projectPermissionBeanMap = initData === null || initData === void 0 ? void 0 : (_initData_userProfile = initData.userProfile) === null || _initData_userProfile === void 0 ? void 0 : (_initData_userProfile_permissions = _initData_userProfile.permissions) === null || _initData_userProfile_permissions === void 0 ? void 0 : _initData_userProfile_permissions.projectPermissionBeanMap;
        return {
            projects: initData === null || initData === void 0 ? void 0 : initData.userProfile.projects.map((project)=>{
                var _projectPermissionBeanMap_project_projectId_toString, _project_projectId;
                return {
                    ...project,
                    permissionSet: (project === null || project === void 0 ? void 0 : project.projectId) ? projectPermissionBeanMap === null || projectPermissionBeanMap === void 0 ? void 0 : (_projectPermissionBeanMap_project_projectId_toString = projectPermissionBeanMap[project === null || project === void 0 ? void 0 : (_project_projectId = project.projectId) === null || _project_projectId === void 0 ? void 0 : _project_projectId.toString()]) === null || _projectPermissionBeanMap_project_projectId_toString === void 0 ? void 0 : _projectPermissionBeanMap_project_projectId_toString.permissionSet : undefined
                };
            }),
            byId: keyBy(initData === null || initData === void 0 ? void 0 : initData.userProfile.projects, 'projectId'),
            isLoading,
            projectPermissionBeanMap
        };
    }, {
        enabled: Boolean(initData)
    });
};
export const useAllCompanies = ()=>{
    return useQuery({
        ...commonOptions,
        select: (data)=>data.companies
    });
};
export const useAccountSignUpMutation = (cookiesEnabled)=>{
    const isCognitoEnabled = localStorage.getItem(COGNITO_ENABLED_KEY) === 'true';
    localStorage.removeItem(COGNITO_ENABLED_KEY);
    return useMutation({
        mutationFn: (payload)=>AccountAPI.signUp(payload),
        onSuccess: (res)=>{
            // Cookies flow: auth via rl_session cookie — redirect to /login to trigger OAuth.
            if (cookiesEnabled) {
                window.location.replace(`https://${res.account.primaryDomainName}/login`);
                return;
            }
            if (isCognitoEnabled) {
                if (res.templateId) {
                    localStorage.setItem(LS_TEMPLATE_ID_KEY, res.templateId.toString());
                }
                window.location.replace(`https://${`${res.account.primaryDomainName}/login`}?token=${res.session.sessionToken}&cognito=${true}`);
                return;
            }
            if (res.templateId) {
                window.location.replace(`https://${res.account.primaryDomainName + RoutePaths.loginWithSession}?token=${res.session.sessionToken}&templateId=${res.templateId}`);
                return;
            }
            window.location.replace(`https://${res.account.primaryDomainName + RoutePaths.loginWithSession}?token=${res.session.sessionToken}`);
        }
    });
};
export const useSignUpThroughGoogle = ()=>{
    return useMutation({
        mutationFn: (payload)=>AccountAPI.signUpThroughGoogle(payload),
        onSuccess: (res)=>{
            if (res.templateId) {
                window.location.replace(`https://${res.account.primaryDomainName + RoutePaths.loginWithSession}?token=${res.session.sessionToken}&templateId=${res.templateId}`);
                return;
            }
            window.location.replace(`https://${res.account.primaryDomainName + RoutePaths.loginWithSession}?token=${res.session.sessionToken}`);
        }
    });
};
export const useIsDomainExistsMutation = ()=>{
    return useMutation({
        mutationFn: (payload)=>AccountAPI.isDomainExists(payload)
    });
};
export const ACCOUNT_PROFILE_KEY = [
    'ACC_PROFILE'
];
export const useAccountProfile = (queryOptions = {})=>useQuery(ACCOUNT_PROFILE_KEY, ()=>AccountAPI.getAccountDetails(), {
        staleTime: GENERAL_API_STALE,
        ...queryOptions
    });
const updateAccTimezoneFn = (data)=>{
    return AccountAPI.updateTimezone(data);
};
export const useAccountTimezoneMutation = (options = {})=>{
    const queryClient = useQueryClient();
    return useMutation((data)=>updateAccTimezoneFn(data), {
        ...options,
        onMutate: async ()=>{
            await queryClient.cancelQueries(ACCOUNT_PROFILE_KEY);
            const previous = queryClient.getQueryData(ACCOUNT_PROFILE_KEY);
            return {
                previous
            };
        },
        onSuccess: async (data, request, context)=>{
            const prev = context === null || context === void 0 ? void 0 : context.previous;
            if (!prev) {
                return;
            }
            queryClient.setQueryData(ACCOUNT_PROFILE_KEY, {
                ...prev,
                timeZone: data.timeZone
            });
        }
    });
};
export const CURRENCY_LIST_KEY = [
    'CURRENCY_LIST'
];
export const useCurrencyListQuery = (queryOptions = {})=>useQuery(CURRENCY_LIST_KEY, ()=>AccountAPI.getCurrencyList(), {
        ...queryOptions,
        staleTime: Infinity
    });
export const useCurrencyMappingListQuery = (queryOptions = {})=>useQuery(CURRENCY_MAPPING_LIST_KEY, ()=>AccountAPI.getCurrencyMappingList(), {
        ...queryOptions
    });
export const useAddCurrencyMappingMutation = (options = {})=>{
    const queryClient = useQueryClient();
    return useMutation((payload)=>AccountAPI.addCurrencyMapping(payload), {
        ...options,
        onSuccess: ()=>{
            queryClient.invalidateQueries(CURRENCY_MAPPING_LIST_KEY);
        }
    });
};
export const useUpdateCurrencyMappingMutation = (options = {})=>{
    const queryClient = useQueryClient();
    return useMutation((payload)=>AccountAPI.updateCurrencyMapping(payload), {
        ...options,
        onSuccess: (_, payload)=>{
            queryClient.invalidateQueries(CURRENCY_MAPPING_LIST_KEY);
            queryClient.invalidateQueries(getCurrencyMappingHistoryKey(payload.currency.currencyCode));
        }
    });
};
// Accept undefined currency: callers (currency settings drawer/modal) can
// pass an undefined currency during the post-mutation re-render when the
// drawer's form state momentarily resets. Destructuring `currencyCode` from
// the parameter directly throws synchronously — even with `enabled: false`
// — because the hook body always runs before React Query consults `enabled`.
export const useCurrencyMappingHistoryQuery = (currency, queryOptions = {})=>{
    var _ref, _queryOptions_enabled;
    return useQuery(getCurrencyMappingHistoryKey((_ref = currency === null || currency === void 0 ? void 0 : currency.currencyCode) !== null && _ref !== void 0 ? _ref : ''), ()=>AccountAPI.getCurrencyMappingHistory(currency.currencyCode), {
        ...queryOptions,
        enabled: currency != null && ((_queryOptions_enabled = queryOptions.enabled) !== null && _queryOptions_enabled !== void 0 ? _queryOptions_enabled : true),
        cacheTime: 0
    });
};
export const useCurrencyMappingAuditLogsQuery = (currency, queryOptions = {})=>{
    var _ref, _queryOptions_enabled;
    return useQuery(getCurrencyMappingAuditLogKey((_ref = currency === null || currency === void 0 ? void 0 : currency.currencyCode) !== null && _ref !== void 0 ? _ref : ''), ()=>AccountAPI.getCurrencyMappingAuditLog(currency.currencyCode).then((data)=>data.map((_data)=>({
                    ..._data,
                    meta: {
                        ..._data.meta,
                        metaType: AuditLogMetaName.Currency
                    }
                }))), {
        ...queryOptions,
        enabled: currency != null && ((_queryOptions_enabled = queryOptions.enabled) !== null && _queryOptions_enabled !== void 0 ? _queryOptions_enabled : true),
        cacheTime: 0
    });
};
export const useUserMetaDataMutation = (userId)=>useMutation((payload)=>UserAPI.updateMetaDataUser(userId, payload));
export const useUpdateCalendarConfigMutation = ()=>{
    return useMutation(getCustomCalendarConfigKey(), (payload)=>AccountAPI.updateCalendarConfig(payload));
};
